import React from 'react';
import { fmt, fmtMoney, niceStep, sum } from './influencedData';

const W = 1100;
const H = 350;

/**
 * Stacked column chart (one stack per period, one segment per channel), drawn as SVG.
 * bars: [{ label, vals: number[], delta: {t, c} | null, clickable }]
 */
export default function InfluencedChart({
  bars, channels, isValue, ariaLabel, canDrill, unitLabel = 'Number of opportunities', stageCode = 'OPP', onPickBar, onSegmentClick, onSegmentHover, onSegmentLeave,
}) {
  const f = isValue ? fmtMoney : fmt;
  const unit = isValue ? 'Pipeline value (USD)' : unitLabel;
  const n = bars.length;
  const L = isValue ? 66 : 58, R = 10, T = 26, B = 78;
  const pw = W - L - R, ph = H - T - B;
  const mx = Math.max(1, ...bars.map((b) => sum(b.vals)));
  const step = niceStep(mx / 4);
  const top = Math.ceil(mx / step) * step;
  const y = (v) => T + ph - (v / top) * ph;
  const colW = pw / n;
  const bw = Math.min(96, colW * 0.56);
  const tiny = n > 8;

  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={ariaLabel}>
      <text transform="rotate(-90)" x={-(T + ph / 2)} y="13" textAnchor="middle" fontSize="12" fontWeight="600" fill="var(--mif-ink2)">{unit}</text>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--mif-line)" />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="var(--mif-muted)">{f(Math.round(v * 100) / 100)}</text>
        </g>
      ))}
      {bars.map((b, i) => {
        const x0 = L + colW * i;
        const x = x0 + (colW - bw) / 2;
        const total = sum(b.vals);
        let acc = 0;
        const ly = T + ph + 36;
        return (
          <g key={b.label}>
            {b.clickable && (
              <rect className="mif-band" x={x0} y={T - 14} width={colW} height={ph + B - 6} rx="10" fill="transparent"
                style={{ cursor: 'pointer' }} onClick={() => onPickBar(i)}>
                <title>{`Show only ${b.label}`}</title>
              </rect>
            )}
            {b.vals.map((val, ci) => {
              if (val <= 0) return null;
              const hh = (val / top) * ph;
              const yt = y(acc + val);
              acc += val;
              return (
                <g key={channels[ci].label}>
                  <rect x={x} y={yt} width={bw} height={hh} fill={channels[ci].color}
                    style={{ cursor: canDrill || b.clickable ? 'pointer' : 'default' }}
                    onMouseEnter={(e) => onSegmentHover(e, i, ci)} onMouseMove={(e) => onSegmentHover(e, i, ci)}
                    onMouseLeave={onSegmentLeave} onClick={() => onSegmentClick(i, ci)} />
                  {hh >= 15 && bw >= (isValue ? 46 : 26) && (
                    <text x={x + bw / 2} y={yt + hh / 2 + 4} textAnchor="middle" fontSize={tiny ? 10.5 : 11.5} fontWeight="600" fill="var(--mif-ink)" pointerEvents="none">{f(val)}</text>
                  )}
                </g>
              );
            })}
            <text x={x + bw / 2} y={y(total) - 7} textAnchor="middle" fontSize={tiny ? 11.5 : 13} fontWeight="700" fill="var(--mif-ink)" pointerEvents="none">{f(total)}</text>
            <text x={x + bw / 2} y={T + ph + 16} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="var(--mif-muted)" pointerEvents="none">{stageCode}</text>
            <text className={b.clickable ? 'mif-lx' : ''} x={x0 + colW / 2} y={ly} textAnchor="middle" fontSize={tiny ? 11.5 : 12.5} fontWeight="600" fill="var(--mif-ink2)"
              style={b.clickable ? { cursor: 'pointer' } : undefined} onClick={b.clickable ? () => onPickBar(i) : undefined}>{b.label}</text>
            {b.delta && (
              <text x={x0 + colW / 2} y={ly + 18} textAnchor="middle" fontSize="11.5" fontWeight="600"
                fill={`var(--mif-${b.delta.c === 'flat' ? 'muted' : b.delta.c})`} pointerEvents="none">{b.delta.text}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
